import { ExcludeBaseIcon } from '@tableau/icons/ExcludeBaseIcon';
import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import { Tooltip } from '@tableau/widgets';
import React, { ReactElement } from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';

import { styles } from './AgentSectionStyles';
import { getTestId } from './AgentSectionsUtils';
import { Agent } from './AgentSectionTypes';

type AgentRowStatusMessageProps = {
  agent: Agent;
  // On-prem only: the reason the feature can't be turned on (e.g. Tableau Server+ required, or not
  // supported with an OAuth custom gateway). When set, it is shown as the status instead of the
  // enabled/trial states below. Null on Cloud and for agents that aren't gated.
  forceDisabledReason?: string | null;
  isAvailable: boolean;
  isTryItMode: boolean;
  tableauAITrial?: ISiteTrial;
}

export const AgentRowStatusMessage = ({
  agent,
  forceDisabledReason,
  isAvailable,
  isTryItMode,
  tableauAITrial
}: AgentRowStatusMessageProps): ReactElement => {
  let icon: JSX.Element = <ExcludeBaseIcon key={agent.id} className={styles.icon} size={14} />;
  let message: string;

  const isTryItEligible = agent.requiresRegistration && isAvailable && isTryItMode;

  if (forceDisabledReason) {
    message = forceDisabledReason;
  } else if (agent.requiresRegistration && !isAvailable) {
    message = isTryItMode
      ? Messages.settings_agents_table_unavailable()
      : Messages.settings_agents_required_salesforce_connection();
  } else if (isTryItEligible && (!tableauAITrial || tableauAITrial.canRestart)) {
    message = Messages.settings_agents_table_not_started();
  } else if (isTryItEligible && tableauAITrial?.status === TrialStatus.EXPIRED) {
    message = Messages.settings_agents_table_requires_tabplus();
  } else if (agent.enabled) {
    icon = <NotificationSuccessIcon key={agent.id} className={styles.icon} size={14} />;
    message = Messages.settings_agents_state_active();
  } else {
    message = Messages.settings_agents_state_inactive();
  }

  return (
    <Tooltip tooltipContent={message}>
      <span className={classes(styles.statusSpan, styles.hasIcon)} data-tb-test-id={getTestId('StatusMessage', agent.id)}>
        {icon}<span>{message}</span>
      </span>
    </Tooltip>
  );
};
